import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Dropdown, Divider, Button } from "semantic-ui-react";
import CustomModal from "src/components/configuracion/components/customModal";
import AsyncButton from 'src/components/general/AsyncButton';
import { GetBankAccounts } from "src/graphql/bankAccounts";
import { AddDocumentsNomina, CreateNomina, GetSelectedBySelectorNominas, GetInProcessNomina, SelectPartialNomina } from "src/graphql/newNomina";
import { importAlertState } from "src/redux/actions/alert";
import { importUpdateNominaInProcessNeeded, importUpdateProvidersData } from "src/redux/actions/proveedores";
import ToNominaRow from "src/components/providers(new)/toNominaRow";
import Banks_SVG from "src/assets/svg/svg_banks";
import moment from "moment";
import { importUpdateNeeded } from "src/redux/actions/flujocaja";
import { NominaStatus } from "src/utils/enum";
import NominaConciliationConflictModal from 'src/components/general/NominaConciliationConflictModal'

/** Evita enviar la misma fracción de nómina dos veces (id documento + selectionId). */
function dedupeNominaSelectedPayload(docs) {
  const seen = new Set();
  return (docs || []).filter((d) => {
    const key = `${String(d?.id ?? "")}::${String(d?.selectionId ?? "")}`;
    if (!d?.id || !d?.selectionId || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Componente compartido para generar nóminas
 * Soporta dos modos:
 * - "providers": Usa GetSelectedBySelectorNominas y muestra documentos con ToNominaRow
 * - "cashflow": Recibe selectedDocuments directamente y los convierte al formato de nómina
 */
export default function GenerateNominaModal({
  companyId,
  // Props para modo providers
  mode = "providers", // "providers" | "cashflow"
  // Props para modo cashflow
  selectedDocuments: cashflowSelectedDocuments = [],
  // Props compartidas
  open,
  onClose,
  initialMode = "new",
  title = "Generar Nómina",
  // Callbacks opcionales
  onSuccess,
  ...props
}) {
  const dispatch = useDispatch();
  const { uid, user } = useSelector((state) => state.auth);
  const { updateNeeded } = useSelector((state) => state.flujocaja);
  
  // Obtener email del usuario de forma robusta
  // El email está en user.email según la estructura de Redux auth (ver auth.js línea 91)
  // También podemos obtenerlo de localStorage como fallback
  const userEmail = useMemo(() => {
    // Intentar obtener del estado de Redux primero
    let email = user?.email;
    
    // Si no está en Redux, intentar obtener de localStorage
    if (!email) {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        email = storedUser?.email;
      } catch (e) {
        console.error('Error al leer usuario de localStorage:', e);
      }
    }
    
    // Si aún no hay email, intentar de user.emails
    if (!email && user?.emails && user.emails.length > 0) {
      email = user.emails[0]?.address;
    }
    
    return email || 'Usuario';
  }, [user?.email, user?.emails, mode, open]);

  // Los hooks siempre deben ejecutarse, pero solo usaremos los datos según el modo
  const getDocsToNomina = GetSelectedBySelectorNominas(companyId);
  const createNewNomina = CreateNomina();
  const addDocumentsToNomina = AddDocumentsNomina();
  const selectPartialNomina = SelectPartialNomina();
  const banks = GetBankAccounts(companyId);

  const [selectedDocsToNomina, setSelectedDocsToNomina] = useState([]);
  const [bankSelected, setBankSelected] = useState();
  const [bankOptions, setBankOptions] = useState([]);
  const [newNomina, setNewNomina] = useState(initialMode);
  const [nominaToAdd, setNominaToAdd] = useState();
  const [nominaName, setNominaName] = useState(`Nómina ${moment().format("DD/MM/YYYY")}`);

  // Conflict modal: shown when attempting to send to nomina a document already bank-reconciled.
  const [showConciliationConflict, setShowConciliationConflict] = useState(false)
  const [conciliationConflictDocs, setConciliationConflictDocs] = useState([])
  const [pendingNominaCreate, setPendingNominaCreate] = useState(null)

  // Obtener nóminas en proceso para la opción de agregar
  const nominasInProcess = GetInProcessNomina({
    companyId,
    status: [NominaStatus.CREATED],
    pagination: { limit: 50, page: 1 }
  });

  const selections = mode === "providers"
    ? (getDocsToNomina?.data?.getSelectedBySelectorNominas || [])
    : [];

  // Convertir documentos de cashflow al formato de selecciones (para usar ToNominaRow)
  const cashflowSelections = useMemo(() => {
    if (mode !== "cashflow" || cashflowSelectedDocuments.length === 0) {
      return [];
    }
    
    return [{
      userId: uid,
      userEmail: userEmail,
      selected: cashflowSelectedDocuments
        .map(doc => {
          // Calcular el monto seleccionable (no el remaining completo)
          const nominaStatus = doc.meta?.nominaStatus || {};
          const remaining = doc.meta?.remaining || doc.amount || 0;
          const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                (nominaStatus.totalSelected || 0) + 
                                (nominaStatus.totalApproved || 0);
          const selectableRemaining = Math.max(0, remaining - totalInNomina);
          
          // Solo incluir documentos con monto seleccionable > 0
          if (selectableRemaining <= 0) return null;
          
          return {
            id: doc.id,
            documentId: doc.meta?.documentCode || doc.id || '0',
            amountSelected: selectableRemaining,
            dateSelected: moment().format('YYYY-MM-DD'),
            documentCode: String(doc.meta?.documentCode || '0'),
            documentType: doc.documentType,
            issuerId: doc.meta?.issuerId || doc.meta?.receiverId || '',
            issuerName: doc.meta?.issuerName || doc.meta?.receiverName || 'Sin nombre',
            selectionId: doc.id,
            selectorId: doc.id,
            typeLabel: doc.meta?.typeLabel || 'Documento',
            userEmail: userEmail // Agregar email del usuario a cada documento
          };
        })
        .filter(doc => doc !== null) // Filtrar documentos nulos
    }];
  }, [mode, cashflowSelectedDocuments, uid, userEmail, user]);

  // Selecciones finales a mostrar (providers o cashflow)
  const finalSelections = mode === "providers" ? selections : cashflowSelections;

  // Generar opciones de nóminas existentes
  const generateNominaOptions = nominasInProcess?.data?.getNominasByCompany?.nominas?.reduce((acc, el) => {
    if (el.status === "CREATED")
      acc.push({ ...el, text: el.name, value: el.id });
    return acc;
  }, []);

  // Efecto para modo providers: cargar documentos seleccionados
  useEffect(() => {
    if (mode === "providers" && selections.length > 0) {
      const currentUserSelection = selections.find((el) => el.userId === uid) || {};
      const currentSelection = currentUserSelection?.selected || [];
      setSelectedDocsToNomina(currentSelection);
    }
  }, [mode, selections.length, getDocsToNomina?.fetching, uid]);

  // Efecto para modo cashflow: convertir documentos al formato de nómina
  useEffect(() => {
    if (mode === "cashflow" && cashflowSelectedDocuments.length > 0) {
      const convertedDocs = cashflowSelectedDocuments
        .map(doc => {
          // Calcular el monto seleccionable (no el remaining completo)
          const nominaStatus = doc.meta?.nominaStatus || {};
          const remaining = doc.meta?.remaining || doc.amount || 0;
          const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                (nominaStatus.totalSelected || 0) + 
                                (nominaStatus.totalApproved || 0);
          const selectableRemaining = Math.max(0, remaining - totalInNomina);
          
          // Solo incluir documentos con monto seleccionable > 0
          if (selectableRemaining <= 0) return null;
          
          return {
            id: doc.id,
            documentId: doc.meta?.documentCode || doc.id || '0',
            amountSelected: selectableRemaining,
            dateSelected: moment().format('YYYY-MM-DD'),
            documentCode: String(doc.meta?.documentCode || '0'),
            documentType: doc.documentType,
            issuerId: doc.meta?.issuerId || doc.meta?.receiverId || '',
            issuerName: doc.meta?.issuerName || doc.meta?.receiverName || '',
            selectionId: doc.id,
            selectorId: doc.id,
            typeLabel: doc.meta?.typeLabel || 'Documento',
            userEmail: userEmail // Agregar email del usuario a cada documento
          };
        })
        .filter(doc => doc !== null); // Filtrar documentos nulos
      setSelectedDocsToNomina(convertedDocs);
    } else if (mode === "cashflow" && cashflowSelectedDocuments.length === 0) {
      setSelectedDocsToNomina([]);
    }
  }, [mode, cashflowSelectedDocuments, userEmail]);

  // Efecto para actualizar cuando se necesita refrescar (modo providers)
  useEffect(() => {
    if (mode === "providers" && updateNeeded?.value) {
      getDocsToNomina.reexecuteQuery({ requestPolicy: "network-only" });
      dispatch(importUpdateNeeded({ value: false }));
    }
  }, [mode, updateNeeded?.value]);

  // Efecto para inicializar el modo cuando se abre el modal
  useEffect(() => {
    if (open) {
      setNewNomina(initialMode);
    }
  }, [open, initialMode]);

  // Efecto para cargar bancos
  useEffect(() => {
    if (!banks.fetching && banks.data) {
      const allBanks = banks.data.bankPasswords.map((f) => {
        return {
          text: f.isActive ? f.name : `${f.name} (desactivada)`,
          content: f.isActive ? undefined : (
            <>
              {f.name} <span style={{ opacity: 0.5 }}>(desactivada)</span>
            </>
          ),
          value: f.id,
          key: f.id,
          image: { avatar: true, src: Banks_SVG(f.templateId) },
          templateId: f.templateId
        };
      });
      setBankOptions(allBanks);
    }
  }, [banks?.fetching]);

  // Funciones para modo providers: agregar/quitar documentos
  function addDocToNomina(doc, adding) {
    if (adding)
      return setSelectedDocsToNomina((oldArr) => {
        if (oldArr.some((item) => item?.selectionId === doc?.selectionId)) return oldArr;
        return [...oldArr, doc];
      });
    const removeDoc = selectedDocsToNomina.filter((f) => f?.selectionId !== doc?.selectionId);
    return setSelectedDocsToNomina(removeDoc);
  }

  function addAllDocsToNomina(doc, adding) {
    if (adding === true) {
      const mergedArr = selectedDocsToNomina.concat(doc);
      const filteredArr = mergedArr?.reduce((acc, obj) => {
        const isValid = !acc.some((item) => item.selectionId === obj.selectionId);
        if (isValid) acc.push(obj);
        return acc;
      }, []);
      return setSelectedDocsToNomina(filteredArr);
    }
    const removeDocs = selectedDocsToNomina?.filter((f) => !doc.some((d) => d.selectionId === f.selectionId));
    return setSelectedDocsToNomina(removeDocs);
  }

  // Función principal para crear nómina
  async function createNomina() {
    try {
      let documentsToProcess = [];

      if (mode === "providers") {
        // Modo providers: usar documentos seleccionados directamente
        documentsToProcess = selectedDocsToNomina.map((element) => {
          return {
            id: element?.id,
            selectionId: element?.selectionId,
            amountSelected: element?.amountSelected,
            dateSelected: element?.dateSelected,
            issuerName: element?.issuerName,
            issuerId: element?.issuerId,
            documentId: element?.documentId,
            documentCode: element?.documentCode,
            documentType: element?.documentType,
            selectorId: element?.selectorId,
            accountId: element?.accountId,
          };
        });
      } else if (mode === "cashflow") {
        // Modo cashflow: validar y convertir documentos
        const validDocuments = cashflowSelectedDocuments.filter(doc => {
          const isNotPaid = !doc.meta?.isPaid;
          const hasRequiredFields = doc.documentType === 'OBLIGATION' || 
            doc.meta?.documentCode || 
            doc.meta?.issuerId ||
            doc.meta?.receiverId;
          const hasValidAmount = (doc.meta?.remaining || doc.amount) > 0;
          
          // Verificar que el documento no esté 100% en nómina
          const nominaStatus = doc.meta?.nominaStatus || {};
          const remaining = doc.meta?.remaining || doc.amount || 0;
          const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                (nominaStatus.totalSelected || 0) + 
                                (nominaStatus.totalApproved || 0);
          const selectableRemaining = remaining - totalInNomina;
          const isNotFullyInNomina = selectableRemaining > 0;
          
          return isNotPaid && hasRequiredFields && hasValidAmount && isNotFullyInNomina;
        });

        if (validDocuments.length === 0) {
          const actionText = newNomina === "new" ? "crear la nómina" : "agregar a la nómina";
          dispatch(
            importAlertState({
              content: `No hay documentos válidos para ${actionText}. Verifica que los documentos no estén pagados, no estén 100% en nómina y tengan la información necesaria.`,
              state: "error",
              duration: 5,
            })
          );
          return;
        }

        // Paso 1: Seleccionar documentos usando SelectPartialNomina
        // Calcular el monto seleccionable para cada documento (no el remaining completo)
        const documentsToSelect = validDocuments.map(doc => {
          const nominaStatus = doc.meta?.nominaStatus || {};
          const remaining = doc.meta?.remaining || doc.amount || 0;
          const original = doc.meta?.originalAmount || doc.amount || 0;
          const conciliated = Math.max(0, Number(original) - Number(remaining))
          const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                (nominaStatus.totalSelected || 0) + 
                                (nominaStatus.totalApproved || 0);
          const selectableRemaining = Math.max(0, remaining - totalInNomina);
          
          return {
            documentId: doc.id,
            amount: selectableRemaining,
            // Keep for conflict modal decision (not sent to backend unless user decides)
            __conciliated: conciliated,
            __title: doc?.meta?.summary || doc?.meta?.typeLabel || 'Documento',
          };
        }).filter(doc => doc.amount > 0); // Filtrar documentos sin monto seleccionable

        // If any document is already bank-reconciled, ask the user whether the amount
        // to send to nomina corresponds to that conciliated amount.
        const conciliatedDocs = documentsToSelect
          .filter(d => (d.__conciliated || 0) > 0)
          .map(d => ({
            documentId: d.documentId,
            title: d.__title,
            matchedAmount: d.__conciliated,
            amountToSelect: d.amount,
          }))

        if (conciliatedDocs.length > 0) {
          setConciliationConflictDocs(conciliatedDocs)
          setPendingNominaCreate({
            validDocuments,
            documentsToSelect,
          })
          setShowConciliationConflict(true)
          return
        }
        
        const selectResult = await selectPartialNomina.executeMutation({ 
          input: documentsToSelect.map(({ __conciliated, __title, ...rest }) => rest)
        });

        if (selectResult.error) {
          console.error('Error selecting documents:', selectResult.error);
          dispatch(
            importAlertState({
              content: "Error al seleccionar documentos para la nómina.",
              state: "error",
              duration: 5,
            })
          );
          return;
        }

        // Paso 2: Parsear la respuesta para obtener los selectionId correctos
        let selectionMap = {}; // Mapa de documentId -> selectionId
        if (selectResult.data?.selectDocumentsForNomina?.data?.[0]) {
          try {
            const parsedData = JSON.parse(selectResult.data.selectDocumentsForNomina.data[0]);
            parsedData.forEach((dte) => {
              if (dte.id && dte.nominaStatus?.inNominaProcess) {
                // Buscar la selección más reciente con status SELECTED para este usuario
                const userSelection = dte.nominaStatus.inNominaProcess.find(
                  (sel) => sel.status === 'SELECTED' && sel.userSelected === uid
                );
                if (userSelection && userSelection.id) {
                  selectionMap[dte.id] = userSelection.id;
                }
              }
            });
          } catch (parseError) {
            console.error('Error parsing selection response:', parseError);
          }
        }

        // Paso 3: Convertir documentos al formato de nómina
        // Usar el monto seleccionable y el selectionId correcto
        documentsToProcess = validDocuments
          .map(doc => {
            const nominaStatus = doc.meta?.nominaStatus || {};
            const remaining = doc.meta?.remaining || doc.amount || 0;
            const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                  (nominaStatus.totalSelected || 0) + 
                                  (nominaStatus.totalApproved || 0);
            const selectableRemaining = Math.max(0, remaining - totalInNomina);
            
            // Obtener el selectionId correcto del mapa, o usar el documentId como fallback
            const selectionId = selectionMap[doc.id] || doc.id;
            
            return {
              id: doc.id,
              documentId: doc.id,
              amountSelected: selectableRemaining,
              dateSelected: moment().format('YYYY-MM-DD'),
              documentCode: String(doc.meta?.documentCode || '0'),
              documentType: doc.documentType,
              issuerId: doc.meta?.issuerId || '',
              issuerName: doc.meta?.issuerName || '',
              selectionId: selectionId,
              selectorId: uid,
            };
          })
          .filter(doc => doc.amountSelected > 0);
      }

      documentsToProcess = dedupeNominaSelectedPayload(documentsToProcess);

      if (documentsToProcess.length === 0) {
        const actionText = newNomina === "new" ? "crear la nómina" : "agregar a la nómina";
        dispatch(
          importAlertState({
            content: `No hay documentos seleccionados para ${actionText}.`,
            state: "error",
            duration: 5,
          })
        );
        return;
      }

      // Crear nueva nómina
      if (newNomina === "new") {
        const input = {
          name: nominaName,
          companyId,
          selectedDocuments: documentsToProcess,
          bankPasswordId: bankSelected,
          bankPasswordTemplateId: bankOptions.find(el => el.value === bankSelected)?.templateId
        };

        const result = await createNewNomina({ input });

        if (result.error && result.error.message?.includes('Cannot return null for non-nullable field')) {
          dispatch(
            importAlertState({
              content: `Nómina creada correctamente con ${documentsToProcess.length} documentos.`,
              state: "success",
              duration: 5,
            })
          );
          dispatch(importUpdateProvidersData(true));
          dispatch(importUpdateNominaInProcessNeeded({ value: true }));
          if (mode === "cashflow") {
            dispatch(importUpdateNeeded({ value: true }));
          }
          if (onSuccess) onSuccess();
          onClose();
        } else if (result.error) {
          console.error('Create nomina error details:', result.error);
          dispatch(
            importAlertState({
              content: `Error al crear nómina: ${result.error.message || 'Error desconocido'}`,
              state: "error",
              duration: 5,
            })
          );
        } else if (result.data) {
          dispatch(
            importAlertState({
              content: `Nómina creada correctamente con ${documentsToProcess.length} documentos.`,
              state: "success",
              duration: 5,
            })
          );
          dispatch(importUpdateProvidersData(true));
          dispatch(importUpdateNominaInProcessNeeded({ value: true }));
          if (mode === "cashflow") {
            dispatch(importUpdateNeeded({ value: true }));
          }
          if (onSuccess) onSuccess();
          onClose();
        }
      }

      // Agregar a nómina existente
      if (newNomina === "add") {
        const result = await addDocumentsToNomina({
          nominaId: nominaToAdd,
          newDocuments: documentsToProcess,
        });

        if (result?.data?.addDocumentsNomina?.status === false) {
          dispatch(
            importAlertState({
              content: result.data.addDocumentsNomina.message || "No se pudieron agregar los documentos a la nómina.",
              state: "error",
              duration: 5,
            })
          );
          return;
        }

        if (result?.error && result.error.message?.includes('Cannot return null for non-nullable field')) {
          dispatch(
            importAlertState({
              content: `${documentsToProcess.length} documentos agregados a la nómina exitosamente.`,
              state: "success",
              duration: 5,
            })
          );
          dispatch(importUpdateProvidersData(true));
          dispatch(importUpdateNominaInProcessNeeded({ value: true }));
          if (mode === "cashflow") {
            dispatch(importUpdateNeeded({ value: true }));
          }
          if (onSuccess) onSuccess();
          onClose();
        } else if (result?.error) {
          console.error('Add documents error:', result.error);
          dispatch(
            importAlertState({
              content: "Hubo un problema al agregar documentos a la nómina, por favor inténtalo nuevamente más tarde.",
              state: "error",
              duration: 5,
            })
          );
        } else if (result?.data) {
          dispatch(
            importAlertState({
              content: `${result.data.addDocumentsNomina.message} (${documentsToProcess.length} documentos agregados).`,
              state: "success",
              duration: 5,
            })
          );
          dispatch(importUpdateProvidersData(true));
          dispatch(importUpdateNominaInProcessNeeded({ value: true }));
          if (mode === "providers") {
            getDocsToNomina.reexecuteQuery({ requestPolicy: "network-only" });
          }
          if (mode === "cashflow") {
            dispatch(importUpdateNeeded({ value: true }));
          }
          if (onSuccess) onSuccess();
          onClose();
        }
      }
    } catch (error) {
      console.error('Error creating nomina:', error);
      dispatch(
        importAlertState({
          content: "Error inesperado al crear la nómina.",
          state: "error",
          duration: 5,
        })
      );
    }
  }

  const continueNominaSelectionAfterConflict = async (decisions) => {
    try {
      const pending = pendingNominaCreate
      if (!pending) return

      const { documentsToSelect, validDocuments } = pending

      const input = documentsToSelect.map(({ __conciliated, __title, ...rest }) => ({
        ...rest,
        imputeMatched: Boolean(decisions?.[rest.documentId]),
      }))

      setShowConciliationConflict(false)
      setPendingNominaCreate(null)

      const selectResult = await selectPartialNomina.executeMutation({ input })
      if (selectResult.error) {
        console.error('Error selecting documents:', selectResult.error);
        dispatch(
          importAlertState({
            content: "Error al seleccionar documentos para la nómina.",
            state: "error",
            duration: 5,
          })
        );
        return
      }

      // Step 2: Parse response to map documentId -> selectionId
      let selectionMap = {}
      if (selectResult.data?.selectDocumentsForNomina?.data?.[0]) {
        try {
          const parsedData = JSON.parse(selectResult.data.selectDocumentsForNomina.data[0]);
          parsedData.forEach((dte) => {
            if (dte.id && dte.nominaStatus?.inNominaProcess) {
              const userSelection = dte.nominaStatus.inNominaProcess.find(
                (sel) => sel.status === 'SELECTED' && sel.userSelected === uid
              );
              if (userSelection && userSelection.id) {
                selectionMap[dte.id] = userSelection.id;
              }
            }
          });
        } catch (parseError) {
          console.error('Error parsing selection response:', parseError);
        }
      }

      // Step 3: Build SelectedDTEInput payload
      const documentsToProcess = validDocuments
        .map(doc => {
          const nominaStatus = doc.meta?.nominaStatus || {};
          const remaining = doc.meta?.remaining || doc.amount || 0;
          const totalInNomina = (nominaStatus.totalNomina || 0) + 
                                (nominaStatus.totalSelected || 0) + 
                                (nominaStatus.totalApproved || 0);
          const selectableRemaining = Math.max(0, remaining - totalInNomina);
          const selectionId = selectionMap[doc.id] || doc.id;
          return {
            id: doc.id,
            documentId: doc.id,
            amountSelected: selectableRemaining,
            dateSelected: moment().format('YYYY-MM-DD'),
            documentCode: String(doc.meta?.documentCode || '0'),
            documentType: doc.documentType,
            issuerId: doc.meta?.issuerId || '',
            issuerName: doc.meta?.issuerName || '',
            selectionId,
            selectorId: uid,
          };
        })
        .filter(doc => doc.amountSelected > 0);

      const documentsToProcessDeduped = dedupeNominaSelectedPayload(documentsToProcess);

      if (documentsToProcessDeduped.length === 0) {
        dispatch(
          importAlertState({
            content: `No hay documentos seleccionados para generar la nómina.`,
            state: "error",
            duration: 5,
          })
        );
        return
      }

      // Continue with create/add flow (same logic as createNomina)
      if (newNomina === "new") {
        const payload = {
          name: nominaName,
          companyId,
          selectedDocuments: documentsToProcessDeduped,
          bankPasswordId: bankSelected,
          bankPasswordTemplateId: bankOptions.find(el => el.value === bankSelected)?.templateId
        };
        const result = await createNewNomina({ input: payload });

        if (result?.error) {
          dispatch(
            importAlertState({
              content: `Error al crear nómina: ${result.error.message || 'Error desconocido'}`,
              state: "error",
              duration: 5,
            })
          );
          return
        }

        dispatch(
          importAlertState({
            content: `Nómina creada correctamente con ${documentsToProcessDeduped.length} documentos.`,
            state: "success",
            duration: 5,
          })
        );
        dispatch(importUpdateProvidersData(true));
        dispatch(importUpdateNominaInProcessNeeded({ value: true }));
        dispatch(importUpdateNeeded({ value: true }));
        if (onSuccess) onSuccess();
        onClose();
      }

      if (newNomina === "add") {
        const result = await addDocumentsToNomina({
          nominaId: nominaToAdd,
          newDocuments: documentsToProcessDeduped,
        });

        if (result?.data?.addDocumentsNomina?.status === false) {
          dispatch(
            importAlertState({
              content: result.data.addDocumentsNomina.message || "No se pudieron agregar los documentos a la nómina.",
              state: "error",
              duration: 5,
            })
          );
          return;
        }

        if (result?.error) {
          dispatch(
            importAlertState({
              content: "Hubo un problema al agregar documentos a la nómina, por favor inténtalo nuevamente más tarde.",
              state: "error",
              duration: 5,
            })
          );
          return
        }

        dispatch(
          importAlertState({
            content: `${documentsToProcessDeduped.length} documentos agregados a la nómina exitosamente.`,
            state: "success",
            duration: 5,
          })
        );
        dispatch(importUpdateProvidersData(true));
        dispatch(importUpdateNominaInProcessNeeded({ value: true }));
        dispatch(importUpdateNeeded({ value: true }));
        if (onSuccess) onSuccess();
        onClose();
      }
    } finally {
      setConciliationConflictDocs([])
    }
  }

  const isValidToCreate = () => {
    // En modo cashflow, validar usando cashflowSelectedDocuments
    // En modo providers, validar usando selectedDocsToNomina
    const hasDocuments = mode === "cashflow" 
      ? cashflowSelectedDocuments.length > 0
      : selectedDocsToNomina.length > 0;
    
    if (newNomina === "new") {
      return hasDocuments && bankSelected && nominaName.trim();
    }
    if (newNomina === "add") {
      return hasDocuments && nominaToAdd && generateNominaOptions?.length > 0;
    }
    return false;
  };

  const handleClose = () => {
    setNewNomina("new");
    setNominaToAdd();
    setNominaName(`Nómina ${moment().format("DD/MM/YYYY")}`);
    setBankSelected();
    if (mode === "providers") {
      setSelectedDocsToNomina([]);
    }
    onClose();
  };

  return (
    <>
    <NominaConciliationConflictModal
      open={showConciliationConflict}
      onClose={() => {
        setShowConciliationConflict(false)
        setPendingNominaCreate(null)
        setConciliationConflictDocs([])
      }}
      documents={conciliationConflictDocs}
      onConfirm={continueNominaSelectionAfterConflict}
    />
    <CustomModal
      dimmer="blurring" 
      size="large" 
      open={open} 
      onClose={handleClose}
      modalStyle={{ minWidth: '1080px' }}
      title={title}
      classNameContent="custom-modal-height"
      {...props}
      content={
        <div className="p-3">
          <div>
            <div className="d-flex align-items-center custom-new-nomina-height">
              <div>
                <input
                  type="radio"
                  name="nomina"
                  value="new"
                  id="new-nomina-option"
                  checked={newNomina === "new"}
                  onChange={() => setNewNomina("new")}
                />
              </div>
              <div className="d-grid w-100">
                <div className="d-flex mb-3 align-items-center">
                  <label
                    htmlFor="new-nomina-option"
                    className="mb-0 ml-3 custom-min-width-nomina-modal"
                  >
                    Crear nómina:
                  </label>
                  <Input
                    disabled={newNomina !== "new"}
                    placeholder="Nueva nómina"
                    value={nominaName}
                    onChange={(e) => setNominaName(e.target.value)}
                    className="w-100"
                    id="new-nomina-name"
                  />
                </div>
                <div className="d-flex align-items-center">
                  <label className="ml-3 custom-min-width-nomina-modal">
                    Cuenta bancaria:{" "}
                  </label>
                  <Dropdown
                    selectOnBlur={false}
                    id="new-nomina-bank-options"
                    disabled={newNomina !== "new"}
                    options={bankOptions}
                    onChange={(_, v) => setBankSelected(v.value)}
                    defaultValue={bankSelected}
                    placeholder="¿Con qué cuenta desea pagar?"
                    clearable
                    selection
                    fluid
                  />
                </div>
              </div>
            </div>
          </div>
          {generateNominaOptions?.length > 0 && (
            <>
              <Divider horizontal>O</Divider>
              <div>
                <div className="d-flex align-items-center">
                  <input
                    type="radio"
                    name="nomina"
                    value="add"
                    checked={newNomina === "add"}
                    onChange={() => setNewNomina("add")}
                    id="old-nomina-option"
                  />
                  <label
                    htmlFor="old-nomina-option"
                    className="mb-0 ml-3 custom-min-width-nomina-modal"
                  >
                    Agregar a una ya existente
                  </label>
                  <Dropdown
                    selectOnBlur={false}
                    id="old-nomina-history-options"
                    upward
                    disabled={newNomina !== "add"}
                    options={generateNominaOptions}
                    onChange={(_, v) => setNominaToAdd(v.value)}
                    placeholder="Elige una nómina"
                    clearable
                    selection
                    fluid
                  />
                </div>
                {generateNominaOptions?.length === 0 && newNomina === "add" && (
                  <div className="alert alert-warning mt-2">
                    <i className="warning circle icon"></i>
                    No hay nóminas creadas disponibles para agregar documentos. Crea una nueva nómina primero.
                  </div>
                )}
              </div>
            </>
          )}
          <Divider className="mt-4" horizontal>
            Seleccionado
          </Divider>
          <div>
            <div className="custom-providers-table px-3">
              {finalSelections?.map((element, index) => {
                return (
                  <ToNominaRow
                    element={element}
                    key={`nom-${index}`}
                    addDocToNomina={addDocToNomina}
                    addAllDocsToNomina={addAllDocsToNomina}
                    selectedDocsToNomina={selectedDocsToNomina}
                  />
                );
              })}
            </div>
          </div>
        </div>
      }
      customActions={
        <div className="tw-flex tw-justify-end tw-gap-2 tw-w-full">
        <Button
          id="cancel-new-nomina"
          onClick={handleClose}
        >
          Cancelar
        </Button>
        <AsyncButton
          id="generate-new-nomina"
          color="primary"
          disabled={!isValidToCreate()}
          onClick={createNomina}
        >
          Generar
        </AsyncButton>
        </div>
      }
    />
    </>
  );
}

